import axios from 'axios';

// Use relative URLs to leverage the proxy configuration in package.json
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword })
};

export const teamsAPI = {
  getAll: () => api.get('/teams'),
  getAllFiltered: (seasonId, activeOnly = false) => api.get('/teams', { 
    params: { 
      seasonId: seasonId, 
      activeOnly: activeOnly ? 'true' : 'false' 
    } 
  }),
  getById: (id) => api.get(`/teams/${id}`),
  getByConference: (conference) => api.get(`/teams/conference/${conference}`),
  getStats: (id, seasonId) => api.get(`/teams/${id}/stats`, { params: { seasonId } }),
  getGames: (id, seasonId, limit) => api.get(`/teams/${id}/games`, { params: { seasonId, limit } }),
  getPlayerStats: (id, seasonId, scope) =>
    api.get(`/teams/${id}/player-stats`, { params: { seasonId, scope } })
};

// Inaktive Spieler (SeasonRosters.isBenched) liefert der Server nur an
// Admins/GMs und nur auf ausdrückliche Anfrage — öffentliche Ansichten lassen
// das Flag weg. Statistikerfassung und Kaderverwaltung setzen es.
const benchedParams = (includeBenched) => (includeBenched ? { includeBenched: true } : undefined);

export const playersAPI = {
  getAll: (includeBenched = false) => api.get('/players', { params: benchedParams(includeBenched) }),
  getFreeAgents: () => api.get('/players/free-agents'),
  getByTeam: (teamId, includeBenched = false) =>
    api.get(`/players/team/${teamId}`, { params: benchedParams(includeBenched) }),
  create: (playerData) => api.post('/players', playerData),
  update: (id, playerData) => api.put(`/players/${id}`, playerData),
  getTradeHistory: (playerId) => api.get(`/players/${playerId}/trade-history`)
};

export const seasonsAPI = {
  getAll: () => api.get('/seasons'),
  getActive: () => api.get('/seasons/active/current'),
  getGmDashboard: (teamId) => api.get(`/seasons/active/gm-dashboard/${teamId}`),
  getGamePeriods: () => api.get('/seasons/active/game-periods'),
  create: (seasonData) => api.post('/seasons', seasonData),
  update: (id, seasonData) => api.put(`/seasons/${id}`, seasonData)
};

export const gamesAPI = {
  getAll: (params) => api.get('/games', { params }),
  getRecent: (limit = 10, seasonId = null) => api.get('/games/recent', { 
    params: { 
      limit, 
      ...(seasonId && { seasonId }) 
    } 
  }),
  getById: (id) => api.get(`/games/${id}`),
  create: (gameData) => api.post('/games', gameData),
  update: (id, data) => api.put(`/games/${id}`, data)
};

export const statsAPI = {
  getPlayerStats: (playerId, seasonId) => api.get(`/stats/player/${playerId}`, { params: { seasonId } }),
  getSeasonSplits: (playerId, type = 'regular') => api.get(`/stats/player/${playerId}/season-splits`, { params: { type } }),
  getCareerAverages: (playerId, type = 'regular') => api.get(`/stats/player/${playerId}/career-averages`, { params: { type } }),
  getLeaders: (category, seasonId, limit = 10) => api.get(`/stats/leaders/${category}`, { params: { seasonId, limit } }),
  getAllLeaders: (seasonId, limit = 5) => api.get('/stats/leaders/all', { params: { seasonId, limit } })
};

export const standingsAPI = {
  get: (seasonId) => api.get('/standings', { params: { seasonId } })
};

export const championsAPI = {
  getAll: () => api.get('/champions'),
  getByOwner: () => api.get('/champions/by-owner'),
  create: (championData) => api.post('/champions', championData)
};

export const adminAPI = {
  generateSeasonGames: (seasonId) => api.post(`/admin/generate/${seasonId}`)
};

export const awardsAPI = {
  getCandidates: (seasonId) => api.get('/awards/candidates', { params: seasonId ? { seasonId } : {} }),
  announce: (data) => api.post('/awards/announce', data),
  openVoting: (data) => api.post('/awards/open-voting', data),
  closeVoting: (data) => api.post('/awards/close-voting', data),
  vote: (data) => api.post('/awards/vote', data),
  getMyVotes: () => api.get('/awards/my-votes'),
  getHistory: () => api.get('/awards/history'),
  getPlayerAwards: (playerId) => api.get(`/awards/player/${playerId}`),
  lockStatLeaders: (seasonId) => api.post('/awards/lock-stat-leaders', seasonId ? { seasonId } : {})
};

// Kaderverwaltung im Admin-Bereich: Spieler aktiv/inaktiv schalten.
export const rosterAPI = {
  getTeamRoster: (teamId, seasonId) =>
    api.get(`/players/team/${teamId}`, {
      params: { includeBenched: true, ...(seasonId ? { seasonId } : {}) }
    }),
  setBenched: (teamId, playerId, isBenched) =>
    api.patch(`/teams/${teamId}/roster/${playerId}/bench`, { isBenched })
};

// Free Agency: die Lesepfade laufen direkt ueber `api`, hier stehen die
// Admin-Aktionen.
export const freeAgencyAPI = {
  // Laufende FA manuell beenden – noetig, weil Verzicht/Timeout die
  // Auto-Completion serverseitig nie ausloesen.
  complete: (freeAgencyId) => api.post(`/free-agency/${freeAgencyId}/complete`)
};

export const rookiesAPI = {
  getAll: () => api.get('/players/rookies'),
  setStatus: (playerId, isRookie) => api.patch(`/players/${playerId}/rookie-status`, { isRookie }),
  clearAll: () => api.post('/players/clear-rookies')
};

export default api;
